<template>
    <div class="content-wrapper-scroll">
        <div class="content-wrapper">
            <div class="row">
                <div class="col-sm-12">
                    <div class="card">
                        <div
                            class="card-header d-flex flex-nowrap align-items-center gap-2 gap-md-3 py-3 border-bottom min-w-0"
                        >
                            <div class="card-title flex-shrink-0 mb-0">
                                <h5 class="mb-0 text-nowrap sap-equipment-page-header__title">SAP Equipment</h5>
                            </div>
                            <div
                                class="card-tools sap-equipment-toolbar d-flex flex-nowrap align-items-center gap-2 ms-auto min-w-0 flex-grow-1"
                            >
                                <button
                                    type="button"
                                    @click="downloadImportFormat"
                                    class="btn btn-secondary btn-sm text-nowrap"
                                >
                                    <i class="fas fa-file-excel me-1"></i>
                                    Download format
                                </button>
                                <div class="sap-excel-import position-relative flex-shrink-0">
                                    <input
                                        ref="excelUploadInput"
                                        type="file"
                                        class="visually-hidden"
                                        accept=".xlsx,.xls,.csv"
                                        tabindex="-1"
                                        aria-hidden="true"
                                        @change="onExcelFileChosen"
                                    />
                                    <div
                                        class="input-group input-group-sm flex-nowrap align-items-stretch sap-excel-import__group"
                                    >
                                        <button
                                            type="button"
                                            class="btn btn-outline-secondary btn-sm text-nowrap flex-shrink-0"
                                            @click="openExcelFilePicker"
                                        >
                                            Choose file
                                        </button>
                                        <span
                                            class="input-group-text flex-grow-1 flex-shrink-1 text-truncate justify-content-start min-w-0 sap-excel-import__label"
                                            :class="excelUploadClass"
                                            :title="excelUploadLabel"
                                        >
                                            {{ excelUploadLabel }}
                                        </span>
                                        <button
                                            type="button"
                                            class="btn btn-primary btn-sm text-nowrap flex-shrink-0"
                                            :disabled="!excelUploadFile"
                                            @click="submitExcelLocationsImport"
                                        >
                                            <i class="fas fa-upload me-1"></i>
                                            Upload
                                        </button>
                                    </div>
                                </div>
                                <button type="button" @click="downloadData" class="btn btn-success btn-sm text-nowrap">
                                    <i class="fas fa-download me-1"></i>
                                    Download QR Codes
                                </button>
                                <router-link to="/sap_equipment/create" class="btn btn-primary btn-sm text-nowrap">
                                    <i class="fas fa-plus me-1"></i>
                                    New SAP Equipment
                                </router-link>
                            </div>
                        </div>

                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-sm-7">
                                    <div class="input-group">
                                        <input
                                            class="form-control"
                                            type="text"
                                            placeholder="Type here and press enter to search"
                                            v-model="meta.search"
                                            @keyup.enter="search"
                                        />
                                        <span class="input-group-text">
                                            <i class="fas fa-search"></i>
                                        </span>
                                    </div>
                                </div>

                                <div class="col-sm-5">
                                    <div class="d-flex gap-2 align-items-stretch">
                                        <select
                                            class="form-control"
                                            v-model="meta.location"
                                            @change="onFilterChange"
                                        >
                                            <option value="">All Locations</option>
                                            <option value="Active">Active Locations</option>
                                            <option value="Inactive">Inactive Locations</option>
                                        </select>
                                        <button
                                            v-if="isSuperAdmin"
                                            type="button"
                                            @click="deleteSelected"
                                            class="btn btn-danger btn-sm text-nowrap flex-shrink-0"
                                            :disabled="selectedEquipments.length == 0"
                                        >
                                            <i class="fas fa-trash me-1"></i>
                                            Bulk Delete
                                        </button>
                                        <button
                                            type="button"
                                            @click="downloadEquipmentsExcel"
                                            class="btn btn-success btn-sm text-nowrap flex-shrink-0"
                                        >
                                            <i class="fas fa-file-excel me-1"></i>
                                            Download
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="table-responsive tabletop">
                                <table class="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th class="text-center" v-if="isSuperAdmin">
                                                <input
                                                    type="checkbox"
                                                    :checked="allSelected"
                                                    @change="toggleSelectAll"
                                                />
                                            </th>
                                            <th>#</th>

                                            <th @click="sort('equipment_code')" class="cursor-pointer">
                                                Equipment Code
                                                <i :class="sortIcon('equipment_code')"></i>
                                            </th>

                                            <th @click="sort('equipment_desc')" class="cursor-pointer">
                                                Equipment Desc
                                                <i :class="sortIcon('equipment_desc')"></i>
                                            </th>

                                            <th @click="sort('latitude')" class="cursor-pointer">
                                                Latitude
                                                <i :class="sortIcon('latitude')"></i>
                                            </th>

                                            <th @click="sort('longitude')" class="cursor-pointer">
                                                Longitude
                                                <i :class="sortIcon('longitude')"></i>
                                            </th>

                                            <th @click="sort('radius')" class="cursor-pointer">
                                                Radius
                                                <i :class="sortIcon('radius')"></i>
                                            </th>

                                            <th @click="sort('geo_fence_sap')" class="cursor-pointer" v-if="isGeoFenceAdmin">
                                                Geo Fencing
                                                <i :class="sortIcon('geo_fence_sap')"></i>
                                            </th>

                                            <th class="cursor-pointer">
                                                Updated At
                                                <i :class="sortIcon('updated_at')"></i>
                                            </th>

                                            <th class="text-center">Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-if="equipments.length === 0">
                                            <td :colspan="emptyColspan" class="text-center">No records found</td>
                                        </tr>

                                        <tr v-for="(equipment, key) in equipments" :key="equipment.sap_equipment_id">
                                            <td class="text-center" v-if="isSuperAdmin">
                                                <input
                                                    type="checkbox"
                                                    :value="equipment.sap_equipment_id"
                                                    v-model="selectedEquipments"
                                                />
                                            </td>
                                            <td>{{ meta.from + key }}</td>
                                            <td>{{ equipment.equipment_code }}</td>
                                            <td>{{ equipment.equipment_desc }}</td>
                                            <td>{{ equipment.latitude }}</td>
                                            <td>{{ equipment.longitude }}</td>
                                            <td>{{ equipment.radius }}</td>
                                            <td class="text-center" v-if="isGeoFenceAdmin">
                                                <input
                                                    type="checkbox"
                                                    :id="'geo_fence_sap_' + equipment.sap_equipment_id"
                                                    :checked="equipment.geo_fence_sap"
                                                    @change="updateSAPGeoFenseEquipment(equipment, $event)"
                                                />
                                            </td>
                                            <td>{{ equipment.updated_at }}</td>

                                            <td class="text-center">
                                                <router-link :to="{name: 'SAPEquipment.Edit', params: { sap_equipment_id: equipment.sap_equipment_id }}" 
                                                    class="icon-action edit"> <i class="icon-pencil"  style="color: green"></i>
                                                </router-link>

                                                <!-- <a href="#" v-if="isSuperAdmin" class="icon-action delete" @click.prevent="deleteRow(equipment)">
                                                    <i class="icon-cancel"></i>
                                                </a> -->
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="form-actions-footer">
                                <div class="row align-items-center">
                                    <div class="col-sm-2">
                                        <select class="form-control" v-model="meta.per_page" @change="onPerPageChange">
                                            <option v-for="n in [10,15,20,25,30]" :key="n">{{ n }}</option>
                                        </select>
                                    </div>

                                    <div class="col-sm-10 text-end">
                                        <Pagination
                                            :maxPage="meta.maxPage"
                                            :totalPages="meta.lastPage"
                                            :currentPage="meta.currentPage"
                                            @pagechanged="onPageChange"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> <!-- .card -->
                </div> <!-- .col-sm-12 -->
            </div> <!-- .row -->
        </div> <!-- .content-wrapper -->
    </div> <!-- .content-wrapper-scroll -->

</template>

<script>
    import Pagination from "@/components/Pagination.vue";
    import axios from "axios";

    export default {
        components: { Pagination },

        computed: {
            excelUploadLabel() {
                if (this.excelUploadFile) {
                    return this.excelUploadFile.name;
                }
                return "No file chosen";
            },
            excelUploadClass() {
                if (this.excelUploadFile) {
                    return "text-dark bg-white";
                }
                return "text-muted bg-white";
            },
            userRoleName() {
                let user = this.$store.getters.user;
                let roleName = "";
                if (user && user.role && user.role.role_name) {
                    roleName = user.role.role_name;
                }
                return roleName;
            },
            isSuperAdmin() {
                if (this.userRoleName == "Super Admin") {
                    return true;
                }
                return false;
            },
            isGeoFenceAdmin() {
                if (this.userRoleName == "Admin") {
                    return true;
                }
                if (this.userRoleName == "Super Admin") {
                    return true;
                }
                return false;
            },
            allSelected() {
                if (!this.equipments || this.equipments.length == 0) {
                    return false;
                }
                let i;
                for (i = 0; i < this.equipments.length; i++) {
                    let id = this.equipments[i].sap_equipment_id;
                    let found = false;
                    let j;
                    for (j = 0; j < this.selectedEquipments.length; j++) {
                        if (this.selectedEquipments[j] == id) {
                            found = true;
                        }
                    }
                    if (!found) {
                        return false;
                    }
                }
                return true;
            },
            emptyColspan() {
                let cols = 8;
                if (this.isSuperAdmin) {
                    cols = cols + 1;
                }
                if (this.isGeoFenceAdmin) {
                    cols = cols + 1;
                }
                return cols;
            },
        },

        data() {
            return {
                equipments: [],
                errors: [],
                meta: {
                    search: "",
                    order_by: "asc",
                    field: "equipment_code",
                    per_page: 10,
                    currentPage: 1,
                    lastPage: 1,
                    from: 1,
                    maxPage: 1,
                    plant_id: this.$store.getters.user.plant_id,
                    location: "",
                },
                excelUploadFile: null,
                selectedEquipments: [],
            };
        },

        mounted() {
            this.syncPlantIdFromUser();
            this.index();
        },

        methods: {
            syncPlantIdFromUser() {
                const user = this.$store.getters.user;
                if (user && user.plant_id) {
                    this.meta.plant_id = user.plant_id;
                }
            },

            index() {
                let loader = this.$loading.show();

                let uri =
                    `paginatewebSAPEquipments?page=${this.meta.currentPage}` +
                    `&search=${this.meta.search}` +
                    `&order_by=${this.meta.order_by}` +
                    `&field=${this.meta.field}` +
                    `&per_page=${this.meta.per_page}` +
                    `&plant_id=${this.meta.plant_id}` +
                    `&location=${this.meta.location}`;

                this.$store
                    .dispatch("post", { uri })
                    .then((res) => {
                        this.equipments = res.data.data;
                        this.meta.lastPage = res.data.meta.last_page;
                        this.meta.from = res.data.meta.from;
                        if (this.meta.lastPage >= 3) {
                            this.meta.maxPage = 3;
                        } else {
                            this.meta.maxPage = this.meta.lastPage;
                        }
                        this.selectedEquipments = [];
                        loader.hide();
                    })
                    .catch(() => loader.hide());
            },

            sort(field) {
                this.meta.field = field;
                if (this.meta.order_by == "asc") {
                    this.meta.order_by = "desc";
                } else {
                    this.meta.order_by = "asc";
                }
                this.index();
            },

            sortIcon(field) {
                if (this.meta.field != field) {
                    return "fas fa-sort";
                }
                if (this.meta.order_by == "asc") {
                    return "fas fa-sort-up";
                }
                return "fas fa-sort-down";
            },

            search() {
                this.meta.currentPage = 1;
                this.index();
            },

            onPageChange(page) {
                this.meta.currentPage = page;
                this.index();
            },

            onPerPageChange() {
                this.meta.currentPage = 1;
                this.index();
            },

            onFilterChange() {
                this.meta.currentPage = 1;
                this.index();
            },

            downloadEquipmentsExcel() {
                const vm = this;
                const token = vm.$store.getters.user && vm.$store.getters.user.token;

                if (!token) {
                    vm.$store.dispatch('error', 'You must be logged in to download data.');
                    return;
                }

                vm.syncPlantIdFromUser();
                const plantId = vm.meta.plant_id;

                if (!plantId) {
                    vm.$store.dispatch('error', 'Plant is required to download data.');
                    return;
                }

                let searchValue = "";
                if (vm.meta.search) {
                    searchValue = vm.meta.search;
                }
                let locationValue = "";
                if (vm.meta.location) {
                    locationValue = vm.meta.location;
                }

                const params = new URLSearchParams({
                    plant_id: plantId,
                    search: searchValue,
                    location: locationValue,
                });

                const loader = vm.$loading.show();
                axios
                    .get(vm.$store.getters.apiUrl + 'downloadSAPEquipmentsExcel?' + params.toString(), {
                        headers: { Authorization: 'Bearer ' + token },
                        responseType: 'blob',
                    })
                    .then(function (response) {
                        let locationSuffix = '';
                        if (vm.meta.location === 'Active') {
                            locationSuffix = '_active';
                        } else if (vm.meta.location === 'Inactive') {
                            locationSuffix = '_inactive';
                        }

                        const blob = new Blob([response.data], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        const dlUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = dlUrl;
                        link.setAttribute(
                            'download',
                            `sap_equipment${locationSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`
                        );
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(dlUrl);
                        vm.$store.dispatch('success', 'SAP equipment data downloaded.');
                    })
                    .catch(function (error) {
                        let msg = "Could not download SAP equipment data.";
                        if (error.response) {
                            if (error.response.data) {
                                if (error.response.data.message) {
                                    msg = error.response.data.message;
                                }
                            }
                        }
                        vm.$store.dispatch("error", msg);
                    })
                    .finally(function () {
                        loader.hide();
                    });
            },

            toggleSelectAll() {
                if (this.allSelected) {
                    this.selectedEquipments = [];
                    return;
                }
                let ids = [];
                let i;
                for (i = 0; i < this.equipments.length; i++) {
                    ids.push(this.equipments[i].sap_equipment_id);
                }
                this.selectedEquipments = ids;
            },

            deleteSelected() {
                if (!this.isSuperAdmin) {
                    return;
                }
                if (this.selectedEquipments.length == 0) {
                    this.$store.dispatch("error", "Please select at least one equipment to delete.");
                    return;
                }
                if (!confirm("Are you sure you want to delete the selected equipment?")) {
                    return;
                }
                this.deleteEquipmentsByIds(this.selectedEquipments);
            },

            deleteRow(equipment) {
                if (!this.isSuperAdmin) {
                    return;
                }
                if (!confirm("Are you sure you want to delete this equipment?")) {
                    return;
                }
                this.deleteEquipmentsByIds([equipment.sap_equipment_id]);
            },

            deleteEquipmentsByIds(ids) {
                let vm = this;
                let loader = vm.$loading.show();
                vm.$store
                    .dispatch("post", {
                        uri: "deleteSAPEquipmentsBulk",
                        data: {
                            sap_equipment_ids: ids,
                        },
                    })
                    .then(function (response) {
                        loader.hide();
                        let msg = "Selected SAP equipment deleted successfully.";
                        if (response.data) {
                            if (response.data.message) {
                                msg = response.data.message;
                            }
                        }
                        vm.$store.dispatch("success", msg);
                        vm.selectedEquipments = [];
                        vm.index();
                    })
                    .catch(function (error) {
                        loader.hide();
                        let msg = "Could not delete selected SAP equipment.";
                        if (error.response) {
                            if (error.response.data) {
                                if (error.response.data.message) {
                                    msg = error.response.data.message;
                                }
                            }
                        }
                        vm.$store.dispatch("error", msg);
                    });
            },

            downloadData() {
                const vm = this;
                const user = vm.$store.getters.user;

                if (!user || !user.token) {
                    vm.$store.dispatch('error', 'You must be logged in to download QR codes.');
                    return;
                }

                vm.syncPlantIdFromUser();
                const plantId = user.plant_id;

                if (!plantId) {
                    vm.$store.dispatch('error', 'Plant is required to download QR codes.');
                    return;
                }

                let searchValue = "";
                if (vm.meta.search) {
                    searchValue = vm.meta.search;
                }
                let locationValue = "";
                if (vm.meta.location) {
                    locationValue = vm.meta.location;
                }

                const params = new URLSearchParams({
                    plant_id: plantId,
                    search: searchValue,
                    location: locationValue,
                    register: "sap_equipment_qr",
                });

                const loader = vm.$loading.show();
                axios
                    .get(
                        vm.$store.getters.apiUrl + 'downloadSAPEquipmentQRCodes?' + params.toString(),
                        {
                            headers: {
                                Authorization: 'Bearer ' + vm.$store.getters.user.token,
                            },
                            timeout: 15000,
                        }
                    )
                    .then(function (response) {
                        let msg = "SAP Equipment QR Codes report queued.";
                        if (response.data) {
                            if (response.data.message) {
                                msg = response.data.message;
                            }
                        }
                        vm.$store.dispatch("success", msg);
                    })
                    .catch(function (error) {
                        let data = null;
                        if (error.response) {
                            data = error.response.data;
                        }
                        let msg = "";
                        if (data) {
                            if (data.message) {
                                msg = data.message;
                            }
                        }

                        if (!msg) {
                            if (data) {
                                if (data.errors) {
                                    let firstError = Object.values(data.errors)[0];
                                    if (Array.isArray(firstError)) {
                                        msg = firstError[0];
                                    } else {
                                        msg = firstError;
                                    }
                                }
                            }
                        }

                        if (!msg) {
                            if (error.code == "ECONNABORTED") {
                                msg = "The request timed out. Please try again.";
                            }
                        }

                        if (!msg) {
                            msg = "Could not start SAP Equipment QR Codes download.";
                        }

                        vm.$store.dispatch("error", msg);
                    })
                    .finally(function () {
                        loader.hide();
                    });
            },

            downloadImportFormat() {
                const vm = this;
                const token = vm.$store.getters.user && vm.$store.getters.user.token;
                const url = vm.$store.getters.apiUrl + 'downloadSAPEquipmentImportFormat';
                const loader = vm.$loading.show();
                axios
                    .get(url, {
                        headers: { Authorization: 'Bearer ' + token },
                        responseType: 'blob',
                    })
                    .then(function (response) {
                        const blob = new Blob([response.data], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        const dlUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = dlUrl;
                        link.setAttribute('download', 'sap_equipment_import_format.xlsx');
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(dlUrl);
                        vm.$store.dispatch('success', 'Import format downloaded.');
                    })
                    .catch(function (error) {
                        let msg = "Could not download import format.";
                        if (error.response) {
                            if (error.response.data) {
                                if (error.response.data.message) {
                                    msg = error.response.data.message;
                                }
                            }
                        }
                        vm.$store.dispatch("error", msg);
                    })
                    .finally(function () {
                        loader.hide();
                    });
            },

            openExcelFilePicker() {
                const el = this.$refs.excelUploadInput;
                if (el) {
                    el.click();
                }
            },

            onExcelFileChosen(event) {
                let file = null;
                if (event.target.files) {
                    if (event.target.files[0]) {
                        file = event.target.files[0];
                    }
                }
                this.excelUploadFile = file;
            },

            submitExcelLocationsImport() {
                const vm = this;
                if (!vm.excelUploadFile) {
                    vm.$store.dispatch("error", "Choose a file first.");
                    return;
                }
                const formData = new FormData();
                formData.append("file", vm.excelUploadFile);
                formData.append("plant_id", vm.meta.plant_id);
                const loader = vm.$loading.show();
                vm.$store
                    .dispatch("uploadFile", {
                        uri: "importSAPEquipmentLocationsExcel",
                        data: formData,
                    })
                    .then(function (response) {
                        let msg = "Upload completed.";
                        if (response.data) {
                            if (response.data.message) {
                                msg = response.data.message;
                            }
                        }
                        let d = {};
                        if (response.data) {
                            d = response.data;
                        }
                        const parts = [];
                        if (d.created != null && d.created > 0) {
                            parts.push(`Created: ${d.created}`);
                        }
                        if (d.updated != null && d.updated > 0) {
                            parts.push(`Updated: ${d.updated}`);
                        }
                        if (d.skipped != null && d.skipped > 0) {
                            parts.push(`Skipped: ${d.skipped}`);
                        }
                        if (d.ignored_blank_rows != null && d.ignored_blank_rows > 0) {
                            parts.push(`Blank rows ignored: ${d.ignored_blank_rows}`);
                        }
                        if (d.skipped_missing_code != null && d.skipped_missing_code > 0) {
                            parts.push(`Missing code: ${d.skipped_missing_code}`);
                        }
                        let extra = "";
                        if (parts.length > 0) {
                            extra = " " + parts.join(". ") + ".";
                        }
                        vm.$store.dispatch("success", msg + extra);
                        vm.index();
                    })
                    .catch(function (error) {
                        let msg = "Upload failed.";
                        if (error.response) {
                            if (error.response.data) {
                                if (error.response.data.message) {
                                    msg = error.response.data.message;
                                }
                            }
                        }
                        vm.$store.dispatch("error", msg);
                    })
                    .finally(function () {
                        loader.hide();
                        vm.excelUploadFile = null;
                        const input = vm.$refs.excelUploadInput;
                        if (input) {
                            input.value = "";
                        }
                    });
            },

            updateSAPGeoFenseEquipment(equipment, event) {
                let vm = this;
                const isChecked = event.target.checked;
                
                let loader = vm.$loading.show();
                vm.$store
                    .dispatch("post", {
                        uri: "updateSAPGeoFenseEquipment",
                        data: {
                            sap_equipment_id: equipment.sap_equipment_id,
                            geo_fence_sap: isChecked
                        }
                    })
                    .then(function (response) {
                        loader.hide();
                        vm.$store.dispatch("success", response.data.message);
                        // Update the local data to reflect the change
                        equipment.geo_fence_sap = isChecked;
                    })
                    .catch(function (error) {
                        loader.hide();
                        vm.errors = error.response.data.errors;
                        vm.$store.dispatch("error", error.response.data.message);
                        // Revert the checkbox if there was an error
                        event.target.checked = !isChecked;
                    });
            },
        },
    };
</script>

<style scoped>
    .sap-equipment-page-header__title {
        font-size: 1.0625rem;
        font-weight: 600;
    }

    .sap-equipment-toolbar {
        overflow-x: auto;
        overflow-y: hidden;
        justify-content: flex-end;
        -webkit-overflow-scrolling: touch;
    }

    .sap-equipment-toolbar > * {
        flex-shrink: 0;
    }

    .sap-excel-import {
        flex: 0 0 auto;
        width: 21rem;
        min-width: 21rem;
        max-width: 21rem;
    }

    .sap-excel-import__group {
        width: 100%;
        flex-wrap: nowrap !important;
    }

    .sap-excel-import__label {
        min-width: 0;
        flex: 1 1 0% !important;
        font-size: 0.8125rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .cursor-pointer {
        cursor: pointer;
    }

    .icon-action i {
        font-size: 1rem;
    }

    .icon-action.edit i {
        color: rgb(29, 47, 242);
    }

    .icon-action.delete i {
        color: red;
    }

    .icon-action {
        margin: 0 5px;
        transition: transform 0.2s ease;
    }

    .icon-action:hover {
        transform: scale(1.2);
    }
</style>
